import { useLazyQuery } from '@apollo/client';
import { LinearProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import { createContext, useEffect, useState } from 'react';
import {
  GET_APP_CONFIGURATION,
  GET_MENU_ITEMS,
} from '../services/configurationQueries/configurationService';
import { FETCH_PATIENT_ID } from '../services/AccountDetailsQueries/AccountDetailsService';
import useAuth from '../services/hooks/useAuth';
import SessionService from '../services/SessionService';
import { STORAGE_KEYS } from '../shared/constants';
import { getAffiliateFromDomain } from '../shared/helper';
import jwt_decode from 'jwt-decode';

const ApplicationContext = createContext('');

export const ApplicationProvider = props => {
  const { enqueueSnackbar } = useSnackbar();
  const { authenticated, logout } = useAuth();
  const [getAppConfig, { loading, data }] = useLazyQuery(
    GET_APP_CONFIGURATION,
    {
      fetchPolicy: 'no-cache',
      onError: err => {
        enqueueSnackbar(
          'Something went wrong while getting Application Configuration. Logging out.',
          {
            variant: 'error',
          }
        );
        logout();
      },
    }
  );

  const [getMenuItems, { loading: menuLoading, data: menuData }] = useLazyQuery(
    GET_MENU_ITEMS,
    {
      fetchPolicy: 'no-cache',
      onError: err => {
        enqueueSnackbar(
          'Something went wrong while fetching Menu Items. Logging out.',
          {
            variant: 'error',
          }
        );
        logout();
      },
    }
  );

  const [getPatientId, {data: patientIdData }] = useLazyQuery(
    FETCH_PATIENT_ID,
    {
      fetchPolicy: 'no-cache',
      onError: err => {
        console.log(err);
      },
    }
  );

  const [userInfo, setUserInfo] = useState({});
  const [menu, setMenu] = useState(null);

  useEffect(() => {
    if (authenticated) {
      const token = SessionService.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const {
        email,
        email_verified: emailVerified,
        name,
        phoneNumber,
      } = jwt_decode(token);
      setUserInfo({
        userInfo: {
          email,
          emailVerified,
          name,
          phoneNumber,
        },
      });
      getAppConfig({
        variables: { affiliateName: getAffiliateFromDomain() },
      });
      getMenuItems();
      getPatientId();
    }
  }, [authenticated, getAppConfig, getMenuItems, getPatientId]);

  useEffect(() => {
    if (menuData) {
      const menuItems = {};
      menuData?.getAppMenuConfig.forEach(item => {
        menuItems[item.name] = {
          subItems: item.subMenuItems,
          icon: item.icon,
          inactiveIcon: item.inactiveIcon,
          route: item.route,
        };
      });
      setMenu({ menuItems: menuItems });
    }
  }, [menuData]);

  useEffect(() => {
    if(patientIdData) {
      window.sessionStorage.setItem("PATIENT_ID", patientIdData.getPatientIdFromkeycloakId.patientId);
    }
  }, [patientIdData])

  if (loading || menuLoading) {
    return <LinearProgress color="primary" />;
  }

  return (
    <ApplicationContext.Provider
      value={
        data && menu
          ? {
              ...JSON.parse(data?.getAppConfig?.appConfig),
              ...userInfo,
              ...menu,
            }
          : { menuItems: null, banner: null }
      }>
      {props.children}
    </ApplicationContext.Provider>
  );
};

export default ApplicationContext;
